// ============================================================================
// The rtc notification-inbox socket
// ============================================================================
/**
 * It is a best-effort **invalidation** channel, never a delivery guarantee:
 * Postgres is the source of truth and everything stays readable over REST if
 * this never connects. Events are not replayed after a reconnect, so callers
 * must re-fetch when the socket opens.
 *
 * Protocol rules the server enforces:
 * - **Server to client only.** Sending any frame makes the server close the
 *   connection, so this module exposes no send API at all.
 * - Authentication happens entirely in the handshake, via a `Bearer_<jwt>`
 *   subprotocol. There is no post-connect auth message and no ticket. In the
 *   browser the only way to set `Sec-WebSocket-Protocol` is the WebSocket
 *   constructor's second argument, which is exactly what it is used for here.
 * - The server pings every 54s and closes anything that has not ponged within
 *   60s. The browser answers pings itself, and cannot be made to send its own.
 *
 * One connection is shared by the whole app: the flow below is ref-counted, so
 * a bell and a list observing at the same time do not open two sockets against
 * the per-user cap of eight.
 */
import TokenManager from '../../utils/tokenManager';
import { BASE_URL } from '../../constants';
import { INBOX_SOCKET_PATH } from '../../constants/notifications';
import { INBOX_EVENT_NOTIFICATION_CREATED, InboxSocketEvent } from './types';

const TAG = 'rtc_socket_tag';

/** Underscore, not a space. A lowercase `bearer_` is also accepted by the server. */
const SUBPROTOCOL_PREFIX = 'Bearer_';

/** Service restart during a deploy — reconnect shortly. */
const CLOSE_SERVICE_RESTART = 1012;
/** Per-user (8) or global connection cap hit — back off hard. */
const CLOSE_TRY_AGAIN_LATER = 1013;

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const RESTART_BACKOFF_MS = 2_000;
const OVER_CAPACITY_BACKOFF_MS = 60_000;

/**
 * Grace period before the socket is torn down after the last subscriber
 * leaves, so a route change between two observing screens does not disconnect
 * and reconnect.
 */
const TEARDOWN_GRACE_MS = 5_000;

const isDev = process.env.NODE_ENV !== 'production';
const log = (message: string) => {
  if (isDev) console.log(`[${TAG}] ${message}`);
};
const logError = (message: string) => console.error(`[${TAG}] ${message}`);

const describeCloseCode = (code: number): string => {
  if (code === 1000) return '(normal)';
  if (code === CLOSE_SERVICE_RESTART)
    return '(service restart — deploy, reconnecting shortly)';
  if (code === CLOSE_TRY_AGAIN_LATER)
    return '(too many connections — per-user cap is 8)';
  return '';
};

const socketUrl = (): string =>
  `${BASE_URL.replace(/^https:/, 'wss:').replace(
    /^http:/,
    'ws:'
  )}${INBOX_SOCKET_PATH}`;

export type InboxSocketHandlers = {
  /** A parsed `notification.created` event. */
  onEvent?: (event: InboxSocketEvent) => void;
  /**
   * The socket (re)connected. Events are never replayed, so anything derived
   * from the inbox has to be re-fetched here.
   */
  onOpen?: () => void;
};

const handlers = new Set<InboxSocketHandlers>();

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = INITIAL_BACKOFF_MS;
let attempt = 0;
/** Set while a connection is wanted; cleared by teardown and by a missing token. */
let running = false;

const clearReconnectTimer = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const scheduleReconnect = (closeCode: number | null, hadOpened: boolean) => {
  if (!running) return;

  // A connection that actually opened starts the next retry from the bottom;
  // otherwise a long-lived socket would inherit an hours-old backoff.
  if (hadOpened) backoffMs = INITIAL_BACKOFF_MS;

  let wait: number;
  if (closeCode === CLOSE_SERVICE_RESTART) {
    wait = RESTART_BACKOFF_MS;
  } else if (closeCode === CLOSE_TRY_AGAIN_LATER) {
    wait = OVER_CAPACITY_BACKOFF_MS;
  } else {
    wait = backoffMs;
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
  }

  // Jitter keeps every client from reconnecting in lockstep after a deploy.
  const jitter = Math.floor(Math.random() * INITIAL_BACKOFF_MS);
  log(
    `SOCKET NOT CONNECTED (closeCode=${closeCode}, hadOpened=${hadOpened}) — retrying in ${
      wait + jitter
    }ms`
  );

  clearReconnectTimer();
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, wait + jitter);
};

async function connect(): Promise<void> {
  if (!running || socket) return;

  // Re-read at every attempt, so a socket re-established after a token refresh
  // carries the new identity — a live socket is never re-validated.
  const token = await TokenManager.getAccessToken();
  if (!running) return;

  if (!token) {
    log('SOCKET NOT CONNECTED — no access token (user is logged out)');
    running = false;
    return;
  }

  attempt += 1;
  // The token itself is never logged; its length is enough to tell "present"
  // from "empty" when a handshake is rejected.
  log(
    `SOCKET CONNECTING (attempt ${attempt}) url=${socketUrl()} tokenLength=${
      token.length
    }`
  );

  let hadOpened = false;
  let ws: WebSocket;
  try {
    ws = new WebSocket(socketUrl(), [`${SUBPROTOCOL_PREFIX}${token}`]);
  } catch (error) {
    logError(`SOCKET ERROR — could not open: ${(error as Error).message}`);
    scheduleReconnect(null, false);
    return;
  }
  socket = ws;

  ws.onopen = () => {
    hadOpened = true;
    backoffMs = INITIAL_BACKOFF_MS;
    log(`SOCKET CONNECTED — negotiatedProtocol=${ws.protocol}`);
    handlers.forEach((handler) => handler.onOpen?.());
  };

  ws.onmessage = (message: MessageEvent) => {
    if (typeof message.data !== 'string') {
      // The server only ever sends text; a binary frame means something is wrong.
      log('SOCKET NEW MESSAGE ignored — binary frame');
      return;
    }

    let event: InboxSocketEvent | null = null;
    try {
      event = JSON.parse(message.data) as InboxSocketEvent;
    } catch (error) {
      logError(
        `SOCKET ERROR — could not parse message: ${(error as Error).message}`
      );
      return;
    }

    // Branch on `event` so future kinds are additive rather than breaking.
    if (event?.event !== INBOX_EVENT_NOTIFICATION_CREATED) {
      log(`SOCKET NEW MESSAGE ignored — unknown event kind '${event?.event}'`);
      return;
    }

    log(
      `SOCKET NEW MESSAGE — event=${event.event} id=${event.notification_id} ` +
        `type=${event.type} unreadCount=${event.unread_count}`
    );
    handlers.forEach((handler) => handler.onEvent?.(event as InboxSocketEvent));
  };

  ws.onerror = () => {
    // The browser deliberately hides the handshake response, so a rejected
    // upgrade (400 = the Bearer_ subprotocol did not reach rtc, 401 = rejected
    // by the auth gateway, 503 = INAPP_ENABLED is not true) only surfaces as a
    // close event with no detail.
    logError('SOCKET ERROR — connection failed');
  };

  ws.onclose = (event: CloseEvent) => {
    log(
      `SOCKET NOT CONNECTED — closed code=${event.code} reason=${
        event.reason
      } ${describeCloseCode(event.code)}`
    );
    if (socket === ws) socket = null;
    scheduleReconnect(event.code, hadOpened);
  };
}

const teardown = () => {
  running = false;
  clearReconnectTimer();
  attempt = 0;
  backoffMs = INITIAL_BACKOFF_MS;
  if (socket) {
    log('SOCKET NOT CONNECTED — tearing down');
    const closing = socket;
    socket = null;
    // Drop the reconnect path before closing, so an intentional teardown does
    // not schedule a retry.
    closing.onclose = null;
    closing.onerror = null;
    closing.close(1000, 'client teardown');
  }
};

/**
 * Observe the inbox socket. The first subscriber opens the connection and the
 * last one closes it, after a short grace period.
 */
export const subscribeToInboxSocket = (
  subscriber: InboxSocketHandlers
): (() => void) => {
  handlers.add(subscriber);

  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }

  if (!running) {
    running = true;
    connect();
  }

  return () => {
    handlers.delete(subscriber);
    if (handlers.size > 0 || teardownTimer) return;
    teardownTimer = setTimeout(() => {
      teardownTimer = null;
      if (handlers.size === 0) teardown();
    }, TEARDOWN_GRACE_MS);
  };
};

/**
 * Whether a socket is currently open. The foreground FCM handler uses this to
 * stay quiet while the socket is delivering the same events — otherwise one
 * notification would raise two toasts in a focused tab.
 */
export const isInboxSocketConnected = (): boolean =>
  socket?.readyState === WebSocket.OPEN;

/** Closes the socket immediately — used on logout, where the token dies. */
export const closeInboxSocket = () => {
  handlers.clear();
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  teardown();
};
